import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { getData, PostdataWithJson } from '../../API/GlobalApi';
import Notifier from '../../Utils/notify';
import SpinnerLoader from '../../Utils/Spinner';
import { reactSelectStyles } from '../../Utils/selectBoxStyle';
import '../../assets/css/Modal.css';

const PROMOTABLE_URL = 'students/admit-card-archive/promotable/';
const PROMOTABLE_OPTIONS_URL = 'students/admit-card-archive/promotable-options/';
const PROMOTE_URL = 'students/admit-card-archive/promote/';

const selectStyles = {
    ...reactSelectStyles,
    control: (base, state) => ({
        ...reactSelectStyles.control?.(base, state),
        minHeight: '38px',
    }),
};

const Step = ({ index, label, children }) => (
    <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px', color: '#7F56DA', marginBottom: '5px' }}>
            {index} {label}
        </div>
        {children}
    </div>
);

export const PromoteFromAdmitCardModal = ({ isOpen, onClose, onPromoted }) => {
    const [course, setCourse] = useState(null);
    const [specialization, setSpecialization] = useState(null);
    const [academicGroup, setAcademicGroup] = useState(null);
    const [lag, setLag] = useState(null);

    // Only cohorts that actually have someone to promote — not every course in
    // the institute. Fetched each time the modal opens, since promoting a class
    // empties it and it should drop out of the list.
    const [courseTree, setCourseTree] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);

    const [students, setStudents] = useState(null);
    const [loading, setLoading] = useState(false);
    const [promoting, setPromoting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setCourse(null); setSpecialization(null); setAcademicGroup(null); setLag(null);
        setStudents(null);

        let cancelled = false;
        (async () => {
            setOptionsLoading(true);
            try {
                const response = await getData(PROMOTABLE_OPTIONS_URL);
                if (!cancelled) setCourseTree(response?.data || []);
            } catch (error) {
                if (!cancelled) setCourseTree([]);
            } finally {
                if (!cancelled) setOptionsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen]);

    const courseOptions = useMemo(
        () =>
            (courseTree || []).map((c) => ({
                label: `${c.name} (${c.count})`,
                value: c.id,
                specializations: c.specializations || [],
            })),
        [courseTree]
    );
    const specOptions = useMemo(
        () =>
            (course?.specializations || []).map((s) => ({
                label: `${s.name} (${s.count})`,
                value: s.id,
                academic_groups: s.academic_groups || [],
            })),
        [course]
    );
    const agOptions = useMemo(
        () =>
            (specialization?.academic_groups || []).map((a) => ({
                label: `${a.name} (${a.count})`,
                value: a.id,
                lags: a.lags || [],
            })),
        [specialization]
    );
    const lagOptions = useMemo(
        () => (academicGroup?.lags || []).map((l) => ({ label: `${l.name} (${l.count})`, value: l.id })),
        [academicGroup]
    );

    // Students load only once all four are chosen — the class alone identifies
    // the cohort, the first three just narrow the choice.
    useEffect(() => {
        if (!isOpen || !course || !specialization || !academicGroup || !lag) {
            setStudents(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const response = await getData(`${PROMOTABLE_URL}?lag_id=${lag.value}`);
                if (cancelled) return;
                setStudents(response?.students || []);
            } catch (error) {
                if (!cancelled) {
                    Notifier.error('Could not load students');
                    setStudents([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, course, specialization, academicGroup, lag]);

    const handlePromote = async () => {
        if (!students?.length) return;
        setPromoting(true);
        try {
            const response = await PostdataWithJson(PROMOTE_URL, {
                archive_ids: students.map((s) => s.archive_id),
            });
            if (response?.error) {
                Notifier.error(response.error);
                return;
            }
            const { succeeded = 0, failed = 0 } = response?.summary || {};
            if (succeeded) Notifier.success(`Promoted ${succeeded} student${succeeded > 1 ? 's' : ''}.`);
            (response?.sections_created || []).forEach((s) =>
                Notifier.success(`Created ${s.name} in ${s.academic_group} (intake ${s.intake_limit}).`)
            );
            (response?.intake_raised || []).forEach((s) =>
                Notifier.success(
                    `Raised intake for ${s.name} in ${s.academic_group}: ${s.from} → ${s.to}.`
                )
            );
            (response?.results || [])
                .filter((r) => !r.success)
                .slice(0, 8)
                .forEach((r) => Notifier.error(`${r.candidate_name || 'Card'}: ${r.error}`));
            if (!succeeded && !failed) Notifier.warn('Nothing was promoted.');

            onPromoted?.();
            onClose();
        } catch (error) {
            Notifier.error('Promotion failed');
        } finally {
            setPromoting(false);
        }
    };

    if (!isOpen) return null;

    const allChosen = course && specialization && academicGroup && lag;
    const target = students?.[0]?.to_academic_group;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                style={{ position: 'relative', width: '78%', maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '26px' }}
            >
                <div className="modal-header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 className="text-primary" style={{ margin: 0 }}>Promote from Admit Cards</h2>
                    <button className="close-btn" onClick={onClose}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M1 1L13 13M13 1L1 13" stroke="#333" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                <p style={{ fontSize: '13px', color: '#6B7280', margin: '6px 0 14px' }}>
                    Pick the class the students are in <strong>now</strong>. Everyone holding a regular admit card for
                    the next semester is listed — the whole class at once, not a page at a time.
                </p>

                <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
                    <Step index="01" label="COURSE">
                        <Select
                            options={courseOptions} value={course}
                            onChange={(v) => { setCourse(v); setSpecialization(null); setAcademicGroup(null); setLag(null); }}
                            placeholder={optionsLoading ? 'Loading…' : courseOptions.length ? 'Select course' : 'Nothing to promote'}
                            isLoading={optionsLoading}
                            isDisabled={optionsLoading || !courseOptions.length}
                            noOptionsMessage={() => 'No course has students ready to promote'}
                            menuPortalTarget={document.body} menuPosition="fixed"
                            isClearable styles={selectStyles}
                        />
                    </Step>
                    <Step index="02" label="SPECIALIZATION">
                        <Select
                            options={specOptions} value={specialization}
                            onChange={(v) => { setSpecialization(v); setAcademicGroup(null); setLag(null); }}
                            placeholder="—" isDisabled={!course} menuPortalTarget={document.body} menuPosition="fixed"
                            isClearable styles={selectStyles}
                        />
                    </Step>
                    <Step index="03" label="ACADEMIC GROUP">
                        <Select
                            options={agOptions} value={academicGroup}
                            onChange={(v) => { setAcademicGroup(v); setLag(null); }}
                            placeholder="—" isDisabled={!specialization} menuPortalTarget={document.body} menuPosition="fixed"
                            isClearable styles={selectStyles}
                        />
                    </Step>
                    <Step index="04" label="CLASS">
                        <Select
                            options={lagOptions} value={lag} onChange={setLag}
                            placeholder="—" isDisabled={!academicGroup} menuPortalTarget={document.body} menuPosition="fixed"
                            isClearable styles={selectStyles}
                        />
                    </Step>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '18px 0 8px', flexShrink: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.6px', color: '#7F56DA' }}>
                        STUDENTS{' '}
                        <span style={{ color: '#6B7280', fontWeight: 500, letterSpacing: 0 }}>
                            {!allChosen
                                ? 'Pick all four above to load the list.'
                                : loading
                                ? 'Loading…'
                                : `${students?.length || 0} eligible${target ? ` · moving to ${target}` : ''}`}
                        </span>
                    </div>
                </div>

                <div style={{ flex: 1, minHeight: '220px', overflowY: 'auto', border: '1px solid #E3E6EB', borderRadius: '8px' }}>
                    {!optionsLoading && !courseOptions.length ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '13px', textAlign: 'center', padding: '0 20px' }}>
                            No student anywhere is holding a regular admit card for their next semester.
                        </div>
                    ) : !allChosen ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '13px' }}>
                            awaiting source selection
                        </div>
                    ) : loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <SpinnerLoader />
                        </div>
                    ) : !students?.length ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '13px', textAlign: 'center', padding: '0 20px' }}>
                            No student in this class has a regular admit card for the next semester.
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ position: 'sticky', top: 0, background: '#F7F7FA' }}>
                                    {['Registration No.', 'Name', 'Mobile', 'Roll No.', 'Moving to'].map((h) => (
                                        <th key={h} style={{ textAlign: 'left', padding: '9px 12px', fontWeight: 700, color: '#6B7280', borderBottom: '1px solid #E3E6EB' }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((s) => (
                                    <tr key={s.archive_id} style={{ borderBottom: '1px solid #F0F1F4' }}>
                                        <td style={{ padding: '8px 12px', color: '#6B7280' }}>{s.permanent_registration_number || '—'}</td>
                                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#222F3E' }}>{s.full_name}</td>
                                        <td style={{ padding: '8px 12px', color: '#6B7280' }}>{s.mobile || '—'}</td>
                                        <td style={{ padding: '8px 12px', color: '#6B7280' }}>{s.roll_number || '—'}</td>
                                        <td style={{ padding: '8px 12px', color: '#1F6F55', fontWeight: 600 }}>
                                            {s.from_academic_group} → {s.to_academic_group}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', flexShrink: 0 }}>
                    <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                        {students?.length
                            ? `Each student keeps their section — ${lag?.label} students move to ${lag?.label} of ${target}, created if it does not exist.`
                            : 'Only regular admit cards one semester ahead are eligible.'}
                    </span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={onClose}
                            style={{ border: '1px solid #D8DBE0', background: 'white', color: '#222F3E', borderRadius: '6px', padding: '8px 18px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handlePromote}
                            disabled={!students?.length || promoting}
                            style={{
                                border: 'none', borderRadius: '6px', padding: '8px 20px', fontSize: '13px', fontWeight: 600, color: '#fff',
                                background: students?.length && !promoting ? '#7F56DA' : '#C7C9D1',
                                cursor: students?.length && !promoting ? 'pointer' : 'not-allowed',
                            }}
                        >
                            {promoting ? 'Promoting…' : `Promote${students?.length ? ` ${students.length}` : ''} →`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PromoteFromAdmitCardModal;
